import type { LinkedinSenderStates } from "./filterLinkedinSenders.util";
import type { Draft } from "./inboxDraft.types";

/** Whose invitation is pending and whose state is unknown (PREC-4216). */
type InviteRelevantStates = Pick<LinkedinSenderStates, "pendingInvitations" | "unknownUserIds">;

interface ResolveInviteNoteFlagParams {
	senderType: "email" | "linkedin" | undefined;
	senderUserId: number | undefined;
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
	senderStates?: InviteRelevantStates;
}

/**
 * Decides whether the first draft in a sequence should be flagged as a LinkedIn
 * invite note (i.e. a connection request) based on the chosen sender's
 * connection state with the prospect.
 *
 * Returns:
 * - true:  flag as invite note (LinkedIn sender is not connected)
 * - false: clear the flag (sender is connected, sender is not LinkedIn, or the sender's own
 *   invitation is already pending — a second one cannot be sent, the message waits for the
 *   acceptance instead, PREC-4216)
 * - undefined: not enough information (connection check in flight, no userId, or the check
 *   failed for this sender — then the option is disabled and nothing goes out)
 */
export const resolveDraftInviteNoteFlag = ({
	senderType,
	senderUserId,
	connectionCheckDone,
	connectedUserIds,
	senderStates,
}: ResolveInviteNoteFlagParams): boolean | undefined => {
	if (senderType !== "linkedin") {
		return false;
	}

	if (!connectionCheckDone || senderUserId == null) {
		return undefined;
	}

	if (senderStates?.pendingInvitations.has(senderUserId)) {
		return false;
	}

	if (senderStates?.unknownUserIds.has(senderUserId)) {
		return undefined;
	}

	return !connectedUserIds.has(senderUserId);
};

/**
 * Applies the auto invite-note flag to the first draft in the array. Drafts at
 * index > 0 are left untouched (they are never auto-flagged — only the first
 * message in a sequence can be a connection request).
 *
 * Returns a new array only when the first draft's flag actually changes.
 */
export const applyAutoInviteFlagToFirstDraft = (
	drafts: Draft[],
	connectionCheckDone: boolean,
	connectedUserIds: Set<number>,
	senderStates?: InviteRelevantStates
): Draft[] => {
	if (drafts.length === 0) {
		return drafts;
	}

	const first = drafts[0];
	const flag = resolveDraftInviteNoteFlag({
		senderType: first.from?.type,
		senderUserId: first.from?.userId,
		connectionCheckDone,
		connectedUserIds,
		senderStates,
	});

	if (flag === undefined) {
		return drafts;
	}

	const current = !!first.isInviteNote;

	if (current === flag) {
		return drafts;
	}

	return drafts.map((d, i) => (i === 0 ? { ...d, isInviteNote: flag || undefined } : d));
};
